import { useEffect, useState, useCallback } from "react";
import { Plus, Trash, Star, Envelope, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge } from "@/components/Shared";
import { ImageCaptionField } from "@/components/ImageCaptionField";
import { CaptionedImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResearchButton } from "@/components/Research";

const TYPES = ["vendor", "seller", "buyer"];
const EMPTY = {
  name: "", type: "vendor", contact_person: "", email: "", phone: "",
  category: "", rating: 0, notes: "", image_id: null, image_caption: "",
};

export default function Partners() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const { data } = await api.get("/partners", { params: { type: tab } });
    setPartners(data);
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/partners", { ...form, rating: Number(form.rating) });
      toast.success("Partner added");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/partners/${id}`);
      toast.success("Partner removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="partners-page">
      <PageHeader
        overline="Supply chain network"
        title="Vendors, Sellers & Buyers"
        description="Everyone you buy from and sell to, in one directory."
        action={
          <Button data-testid="add-partner-button" onClick={() => setOpen(true)} className="h-11 w-full rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A] sm:h-10 sm:w-auto">
            <Plus size={16} className="mr-2" /> Add partner
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4 min-w-0">
        <TabsList className="tab-strip no-scrollbar rounded-none bg-slate-100">
          <TabsTrigger data-testid="partner-filter-all" value="all" className="rounded-none text-xs uppercase tracking-wider">All</TabsTrigger>
          {TYPES.map((t) => (
            <TabsTrigger key={t} data-testid={`partner-filter-${t}`} value={t} className="rounded-none text-xs uppercase tracking-wider">{t}s</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {partners.length === 0 ? (
        <EmptyState testid="partners-empty" title="No partners yet" description="Add a vendor, seller or buyer to get started." />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {partners.map((p) => (
            <div key={p.id} data-testid={`partner-card-${p.id}`} className="h-full border border-border bg-white p-4 transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display text-base font-semibold tracking-tight text-[#0F172A]">{p.name}</div>
                  <div className="mt-1.5"><StatusBadge value={p.type} /></div>
                </div>
                <div className="flex items-center gap-1 font-mono text-xs text-slate-500">
                  <Star size={13} weight="fill" className="text-[#FFCC00]" />
                  {p.rating || "—"}
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-xs text-slate-500">
                {p.category && <div className="uppercase tracking-wider">{p.category}</div>}
                {p.contact_person && <div className="font-medium text-[#0F172A]">{p.contact_person}</div>}
                {p.email && <div className="flex items-center gap-1.5"><Envelope size={13} /> {p.email}</div>}
                {p.phone && <div className="flex items-center gap-1.5"><Phone size={13} /> {p.phone}</div>}
              </div>
              <CaptionedImage
                testid={`partner-image-${p.id}`}
                imageId={p.image_id}
                caption={p.image_caption}
                className="h-36 w-full border border-border object-cover"
              />
              {user?.role === "admin" && (
                <button data-testid={`delete-partner-${p.id}`} onClick={() => remove(p.id)} className="mt-4 flex items-center gap-1.5 border border-border px-2 py-1 text-xs text-[#FF3B30] transition-colors duration-150 hover:bg-[#FFECEB]">
                  <Trash size={12} /> Remove
                </button>
              )}
              {["admin", "vendor", "buyer"].includes(user?.role) && (
                <div className="mt-3">
                  <ResearchButton
                    label="Check this company"
                    endpoint="/research/supplier"
                    body={{ partner_id: p.id }}
                    testid={`research-partner-${p.id}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Add partner</DialogTitle>
            <DialogDescription>Add a vendor, seller or buyer to the directory.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="add-partner-form">
            <div>
              <Label className="overline text-slate-500">Company name</Label>
              <Input data-testid="partner-name-input" value={form.name} onChange={set("name")} required className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="overline text-slate-500">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="partner-type-select" className="mt-1.5 rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-slate-500">Category</Label>
                <Input data-testid="partner-category-input" value={form.category} onChange={set("category")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Contact person</Label>
                <Input data-testid="partner-contact-input" value={form.contact_person} onChange={set("contact_person")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Rating (0-5)</Label>
                <Input data-testid="partner-rating-input" type="number" step="0.1" max="5" min="0" value={form.rating} onChange={set("rating")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Email</Label>
                <Input data-testid="partner-email-input" value={form.email} onChange={set("email")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
              <div>
                <Label className="overline text-slate-500">Phone</Label>
                <Input data-testid="partner-phone-input" value={form.phone} onChange={set("phone")} className="mt-1.5 rounded-none border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="overline text-slate-500">Notes</Label>
              <Textarea data-testid="partner-notes-input" value={form.notes} onChange={set("notes")} className="mt-1.5 rounded-none border-slate-300" />
            </div>
            <ImageCaptionField
              testid="partner-image"
              value={form.image_id}
              caption={form.image_caption}
              onChange={({ image_id, image_caption }) =>
                setForm((f) => ({ ...f, image_id, image_caption }))
              }
            />
            <DialogFooter>
              <Button data-testid="save-partner-button" type="submit" className="rounded-none bg-[#002FA7] text-xs font-semibold uppercase tracking-widest hover:bg-[#00227A]">
                Save partner
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
